import React, { useState, useEffect } from 'react';
import { WorkoutTemplateData } from '../../../types';
import { apiServices } from '../../../api/services';
import { BaseApiService } from '../../../api/services/BaseApiService';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../i18n/provider';
import CreateTemplateModal from './CreateTemplateModal';
import ConfirmationModal from '../../common/ConfirmationModal';
import styles from './HomeWorkoutTemplates.module.css';

 let seedExercisesCachePromise: Promise<unknown> | null = null;
 const ensureExercisesCacheSeeded = async () => {
  if (!seedExercisesCachePromise) {
    seedExercisesCachePromise = apiServices.exercises.getExercises().catch(() => []);
  }
  await seedExercisesCachePromise;
 };

// Component to display individual exercise with fetched name
const TemplateExerciseItem: React.FC<{ exercise: any; index: number; template: WorkoutTemplateData; onExpandTemplate: (template: WorkoutTemplateData) => void }> = ({ exercise, index, template, onExpandTemplate }) => {
  const { t } = useTranslation();
  const [exerciseName, setExerciseName] = useState(t('workout.templates.exerciseFallback', { count: index + 1 }));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchExerciseName = async () => {
      try {
        const exerciseDef = await apiServices.exercises.getExerciseById(exercise.exerciseId);
        if (exerciseDef) {
          setExerciseName(exerciseDef.localizedName || exerciseDef.name);
        }
      } catch (error) {
        console.warn(`Failed to fetch exercise name for ID ${exercise.exerciseId}:`, error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExerciseName();
  }, [exercise.exerciseId]);

  // TODO.md E, item 5: unit === 'sec' means a hold-time exercise (stretching/mobility) - render
  // "Xs hold" instead of "X reps @ Y kg".
  const summaryKey = exercise.unit === 'sec' ? 'workout.templates.exerciseSummaryHold' : 'workout.templates.exerciseSummary';

  return (
    <div className={styles.templateExercise}>
      {isLoading ? t('workout.loading.templates') : t(summaryKey, {
        name: exerciseName,
        sets: exercise.sets,
        reps: exercise.reps,
        weight: exercise.weight,
        unit: exercise.unit,
      })}
    </div>
  );
};

 const TemplateExerciseNameItem: React.FC<{ exerciseId: string; templateId: string; exerciseDetails?: { sets: number; reps: number; weight: number; unit: string }; onNavigateToExercise?: (exerciseId: string, templateId?: string) => void }> = ({ exerciseId, templateId, exerciseDetails, onNavigateToExercise }) => {
  const { t } = useTranslation();
  const [exerciseName, setExerciseName] = useState(t('workout.loading.templates'));
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchExerciseName = async () => {
      try {
        await ensureExercisesCacheSeeded();
        const exerciseDef = await apiServices.exercises.getExerciseById(exerciseId);
        if (!cancelled) {
          setExerciseName(exerciseDef?.localizedName || exerciseDef?.name || exerciseId);
          // externalUrl is dead - the backend stopped returning it after migration 15 merged it into
          // image_url (TODO.md B3/F4); imageUrl is the only field populated now.
          const resolved = BaseApiService.resolveImageUrl(exerciseDef?.imageUrl);
          setImageUrl(resolved || null);
        }
      } catch {
        if (!cancelled) {
          setExerciseName(exerciseId);
          setImageUrl(null);
        }
      }
    };

    fetchExerciseName();
    return () => {
      cancelled = true;
    };
  }, [exerciseId]);

  return (
    <div
      className={styles.detailExerciseRow}
      data-testid="template-exercise"
      onClick={() => {
        if (!onNavigateToExercise) return;
        if (!exerciseName || exerciseName === t('workout.loading.templates')) return;
        onNavigateToExercise(exerciseId, templateId);
      }}
      role={onNavigateToExercise ? 'button' : undefined}
      tabIndex={onNavigateToExercise ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onNavigateToExercise) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        if (!exerciseName || exerciseName === t('workout.loading.templates')) return;
        onNavigateToExercise(exerciseId, templateId);
      }}
    >
      {imageUrl ? (
        <img className={styles.detailExerciseImage} src={imageUrl} alt={exerciseName} loading="lazy" />
      ) : (
        <div className={styles.detailExerciseImagePlaceholder} />
      )}
      <div className={styles.detailExerciseName}>{exerciseName}</div>
      {exerciseDetails && (
        <div className={styles.exerciseDetails}>
          {/* TODO.md E, item 5: this row isn't localized elsewhere in this component, so the hold-time
              branch stays plain text too rather than introducing t() for just this one line. */}
          {exerciseDetails.unit === 'sec'
            ? `${exerciseDetails.sets} sets × ${exerciseDetails.reps}s hold`
            : `${exerciseDetails.sets} sets × ${exerciseDetails.reps} reps @ ${exerciseDetails.weight} ${exerciseDetails.unit}`}
        </div>
      )}
    </div>
  );
 };

interface HomeWorkoutTemplatesProps {
  templates: WorkoutTemplateData[];
  onSelectTemplate: (template: WorkoutTemplateData) => void;
  onLoadTemplate: (template: WorkoutTemplateData) => void;
  onTemplatesRefresh?: () => void;
  onNavigateToExercise?: (exerciseId: string, templateId?: string) => void;
  selectedTemplateId?: string | null;
  onTemplateSelected?: (templateId: string | null) => void;
}

const HomeWorkoutTemplates: React.FC<HomeWorkoutTemplatesProps> = ({ templates, onSelectTemplate, onLoadTemplate, onTemplatesRefresh, onNavigateToExercise, selectedTemplateId, onTemplateSelected }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState<WorkoutTemplateData | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<WorkoutTemplateData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleTemplateClick = (template: WorkoutTemplateData) => {
    setSelectedTemplate(selectedTemplate === template.id ? null : template.id);
  };

  const handleExpandTemplate = (template: WorkoutTemplateData) => {
    setExpandedTemplate(template);
    if (onTemplateSelected) {
      onTemplateSelected(template.id);
    }
  };

  const handleCloseTemplateDetail = () => {
    setExpandedTemplate(null);
    if (onTemplateSelected) {
      onTemplateSelected(null);
    }
  };

  // Auto-expand template when selectedTemplateId is provided
  React.useEffect(() => {
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        setExpandedTemplate(template);
        setSelectedTemplate(template.id);
      }
      return;
    }

    if (!selectedTemplateId) {
      setSelectedTemplate(null);
    }
  }, [selectedTemplateId, templates]);

  useEffect(() => {
    if (!expandedTemplate) return;
    const updated = templates.find((t) => t.id === expandedTemplate.id);
    if (updated) {
      setExpandedTemplate(updated);
    }
  }, [templates, expandedTemplate]);

  const handleStartWorkout = () => {
    const template = templates.find(t => t.id === selectedTemplate);
    if (template) {
      onLoadTemplate(template);
    }
  };

  const handleTemplateCreated = () => {
    // Template was created successfully
    if (onTemplatesRefresh) {
      onTemplatesRefresh();
    }
  };

  const handleConfirmDelete = async () => {
    if (!expandedTemplate?.id) return;

    try {
      setIsDeleting(true);
      await apiServices.templates.deleteTemplate(expandedTemplate.id);
      setShowDeleteConfirm(false);
      handleCloseTemplateDetail();
      if (onTemplatesRefresh) {
        onTemplatesRefresh();
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert(t('workout.templates.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const renderTemplateList = (templates: WorkoutTemplateData[], isActive: boolean, title: string) => (
    <div className={`${styles.templateSection} ${isActive ? styles.active : styles.inactive}`}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <ul className={styles.templatesList} data-testid="templates-list">
        {templates.map((template) => (
          <div key={template.id}>
            <li
              className={`${styles.templateItem} ${selectedTemplate === template.id ? 'selected' : ''}`}
              data-testid="template-item"
              onClick={() => handleTemplateClick(template)}
            >
              <h3 className={styles.templateName}>{template.localizedName || template.name}</h3>
            </li>
            
            {selectedTemplate === template.id && (
              <>
                <div className={styles.templateActions}>
                  <button
                    className={styles.moreDetailsBtn}
                    data-testid="template-more-details-button"
                    onClick={() => {
                      handleExpandTemplate(template);
                    }}
                  >
                    {t('workout.templates.moreDetails')}
                  </button>
                </div>
                <div className={styles.templateExercises}>
                  {template.exercises.map((exercise: any, idx: number) => (
                    <TemplateExerciseItem 
                      key={idx} 
                      exercise={exercise} 
                      index={idx} 
                      template={template}
                      onExpandTemplate={handleExpandTemplate}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
        
        {templates.length === 0 && (
          <div className={styles.emptyMessage}>
            <p>{user ? t('workout.templates.empty') : t('workout.templates.anonymousDisabled')}</p>
          </div>
        )}
      </ul>
    </div>
  );

  return (
    <div className={styles.templatesContent}>
      <div className="page-content">
        <div className={`content-card ${styles.contentCard}`}>
        {expandedTemplate && (
          <div data-testid="template-details">
            <h2 className="content-title">{t('workout.templates.detailTitle')}</h2>
            <button
              className={styles.closeButton}
              onClick={handleCloseTemplateDetail}
              aria-label={t('workout.templates.close')}
            >
              ✕
            </button>

            {expandedTemplate.userId && expandedTemplate.userId === user?.id && (
              <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                <button
                  className="button button--secondary"
                  onClick={() => {
                    setEditingTemplate(expandedTemplate);
                    setShowCreateModal(true);
                  }}
                >
                  {t('workout.templates.editTemplate')}
                </button>
                <button
                  className="button button--danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('workout.templates.deleting') : t('workout.templates.delete')}
                </button>
              </div>
            )}
            <h3>{expandedTemplate.localizedName || expandedTemplate.name}</h3>
            {(expandedTemplate.localizedDescription || expandedTemplate.description) && <p>{expandedTemplate.localizedDescription || expandedTemplate.description}</p>}

            <div>
              {expandedTemplate.exercises.map((ex, idx) => (
                <TemplateExerciseNameItem
                  key={`${ex.exerciseId}-${idx}`}
                  exerciseId={ex.exerciseId}
                  templateId={expandedTemplate.id}
                  exerciseDetails={{ sets: ex.sets, reps: ex.reps, weight: ex.weight, unit: ex.unit }}
                  onNavigateToExercise={onNavigateToExercise}
                />
              ))}
            </div>
          </div>
        )}
        {!expandedTemplate && (
          <>
            <h1 className="content-title">{t('workout.templates.title')}</h1>
            {/* Templates List */}
            <div className={styles.templatesContainer}>
              {renderTemplateList(templates, true, t('workout.templates.allTemplates'))}
            </div>
          </>
        )}
        
      </div>

      {!showCreateModal && (
        <div className="action-buttons">
          {selectedTemplate ? (
            <>
              <button
                className="button button--primary"
                data-testid="start-workout-button"
                onClick={handleStartWorkout}
                style={{ flex: 1 }}
              >
                <span className={styles.buttonIcon}>▶️</span>
                <span>{t('workout.templates.startWorkout')}</span>
              </button>
              <button
                className="button button--secondary"
                onClick={() => setShowCreateModal(true)}
                style={{ width: 48, minWidth: 48, flex: '0 0 auto', padding: 0 }}
                aria-label={t('workout.templates.newTemplate')}
              >
                <span className={styles.buttonIcon}>+</span>
              </button>
            </>
          ) : (
            <button
              className="button button--secondary"
              onClick={() => setShowCreateModal(true)}
              style={{ flex: 1 }}
              disabled={!user}
              title={!user ? t('workout.templates.anonymousDisabled') : undefined}
            >
              <span className={styles.buttonIcon}>+</span>
              <span>{t('workout.templates.newTemplate')}</span>
            </button>
          )}
        </div>
      )}
      </div>

      <CreateTemplateModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingTemplate(null);
        }}
        onTemplateCreated={handleTemplateCreated}
        initialTemplate={editingTemplate || undefined}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={t('workout.templates.deleteTitle')}
        message={t('workout.templates.deleteMessage')}
        confirmText={isDeleting ? t('workout.templates.deleting') : t('workout.templates.delete')}
        cancelText={t('workout.today.cancel')}
        isDangerous={true}
        onCancel={() => {
          if (isDeleting) return;
          setShowDeleteConfirm(false);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default HomeWorkoutTemplates;
